import { withKeycloak } from "@react-keycloak/web";
import debounce from "lodash.debounce";
import React from "react";
import { withRouter } from "react-router-dom";
import { labels } from "../../../../../localization/labels";
import { activeFilterPageInfo } from "../../../../../services/FilteringUtils";
import KnowledgeCopyrightService from "../../../../../services/KnowledgeCopyrightService";
import KnowledgeService from "../../../../../services/KnowledgeService";
import KnowledgeSourceService from "../../../../../services/KnowledgeSourceService";
import KnowledgeTypeService from "../../../../../services/KnowledgeTypeService";
import CommonUtil from "../../../../../utils/CommonUtil";
import PathUtil from "../../../../../utils/PathUtil";
import BasicDialog from "../../../../core/BasicDialog/BasicDialog";
import { paths } from "../../../../paths";
import KnowledgeSourceLookup from "../../../Lookups/KnowledgeSourceLookup";
import KnowledgeTypeLookup from "../../../Lookups/KnowledgeTypeLookup";
import PageComponent from "../../../page/PageComponent";
import SearchSimilar from "../../../page/SearchSimilar";
import { calcKnowledgeItemSearchSimilar } from "../../../page/SearchSimilar/SearchSimilar";
import {
  knowledgeItemBasicValidationRules as validationRules,
  validateEntity
} from "./../../../../../validators/validationRules";

class KnowledgeItemBasicContainer extends PageComponent {
  constructor(props) {
    super(props);

    this.knowledgeService = new KnowledgeService(this.keycloak);
    this.knowledgeTypeService = new KnowledgeTypeService(this.keycloak);
    this.knowledgeCopyrightService = new KnowledgeCopyrightService(
      this.keycloak
    );
    this.knowledgeSourceService = new KnowledgeSourceService(this.keycloak);

    this.documentRef = React.createRef();

    this.state = this.initialState();

    this.setSearchSimilar = debounce(this.setSearchSimilar, 500);
    this.startUploading = null;
  }

  getComponentTitle = () => {
    return labels.knowledgeItem;
  };

  initialState() {
    return {
      knowledgeItem: {
        noDescription: true,
        dateFromTo: {
          dateFrom: new Date()
        }
      },
      disabled: true
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadKnowledgeCopyrights();
    this.loadKnowledgeTypes();
    this.loadKnowledgeSources();
  }

  loadKnowledgeCopyrights = () => {
    this.prepareCall(
      this.knowledgeCopyrightService.getEntities(
        activeFilterPageInfo,
        this.sortInfo,
        this.onKnowledgeCopyrightsLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadKnowledgeTypes = () => {
    const sortInfo = { orderBy: "name", direction: "asc" };
    this.prepareCall(
      this.knowledgeTypeService.getEntities(
        activeFilterPageInfo,
        sortInfo,
        this.onKnowledgeTypeLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadKnowledgeSources = () => {
    const sortInfo = { orderBy: "name", direction: "asc" };
    this.prepareCall(
      this.knowledgeSourceService.getEntities(
        activeFilterPageInfo,
        sortInfo,
        this.onKnowledgeSourcesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onKnowledgeCopyrightsLoad = data => {
    const knowledgeCopyrights = CommonUtil.getDataContent(data);
    this.setState({ knowledgeCopyrights: knowledgeCopyrights });
  };

  onKnowledgeTypeLoad = data => {
    const knowledgeTypes = CommonUtil.getDataContent(data);
    this.setState({ knowledgeTypes: knowledgeTypes });
  };

  onKnowledgeSourcesLoad = data => {
    const knowledgeSources = CommonUtil.getDataContent(data);
    this.setState({ knowledgeSources: knowledgeSources });
  };

  inputFields = () => {
    const inputFields = [
      {
        label: labels.knowledgeSource,
        controlId: "knowledgeSource",
        content: (
          <KnowledgeSourceLookup
            onChange={this.handleDialogChange}
            controlId="knowledgeSource"
            selected={this.state.knowledgeItem?.knowledgeSource}
            messageHandler={this.messageHandler}
            placeholder={labels.knowledgeSource}
            required={
              validationRules.knowledgeItemBasic.knowledgeSource?.required
            }
          />
        )
      },
      {
        label: labels.knowledgeCopyright,
        controlId: "knowledgeCopyright",
        placeholder: labels.knowledgeCopyright,
        type: "dropdown",
        dropDownOptions: this.state.knowledgeCopyrights
      },
      {
        label: labels.knowledgeType,
        controlId: "knowledgeType",
        content: (
          <KnowledgeTypeLookup
            onChange={this.handleDialogChange}
            controlId="knowledgeType"
            selected={this.state.knowledgeItem?.knowledgeType}
            messageHandler={this.messageHandler}
            placeholder={labels.knowledgeType}
            required={
              validationRules.knowledgeItemBasic.knowledgeType?.required
            }
          />
        )
      },
      {
        label: labels.uri,
        controlId: "uri",
        placeholder: labels.uriPlaceholder,
        type: "text"
      },
      {
        label: labels.document,
        contorlId: "document",
        type: "document",
        quickCreate: true,
        editable: true
      }
    ];
    return inputFields;
  };

  handleDialogChange = event => {
    const knowledgeItem = this.state.knowledgeItem;
    knowledgeItem[event.target.id] = event.target.value;
    const disabled = !this.validator(knowledgeItem);
    this.setState({
      knowledgeItem,
      disabled
    });
    if (typeof event.target.value !== "object") {
      this.setSearchSimilar(knowledgeItem);
    }
  };

  setSearchSimilar = entity => {
    const searchSimilarObject = calcKnowledgeItemSearchSimilar(entity);
    this.setState(searchSimilarObject);
  };

  validator = entity => {
    return validateEntity(entity, validationRules.knowledgeItemBasic);
  };

  handleEntered() {
    this.focusInput();
  }

  onCreateData = () => {
    const { knowledgeItem } = this.state;
    this.prepareCall(
      this.knowledgeService.postEntity(
        knowledgeItem,
        this.onDataResponse,
        this.messageHandler.handleError,
        this.onSaveSuccessMessage
      )
    );
    this.performCalls();
  };

  onDataResponse = data => {
    this.startUploading && this.startUploading(data);
    this.setState({ ...this.initialState() });
    this.props.handleClose();
    this.changePath(
      PathUtil.itemPath(paths.KNOWLEDGE_ITEM, data.id),
      data.name
    );
  };

  createNewKnowledgeSource = name => {
    return {
      name: name,
      dateFromTo: {
        dateFrom: new Date()
      }
    };
  };

  handleCreateOption = (value, propertyId) => {
    const { knowledgeItem, knowledgeSources } = this.state;
    knowledgeItem[propertyId] = value;

    if (propertyId === "knowledgeSource") {
      CommonUtil.insertToArrayBeginning(knowledgeSources, value);
    }

    const disabled = !this.validator(knowledgeItem);
    this.setState({
      knowledgeItem,
      disabled,
      knowledgeSources
    });
  };

  setStartUploading = startUploading => {
    this.startUploading = startUploading;
  };

  render() {
    const maxLength = {
      name: 255
    };

    return (
      <BasicDialog
        dialogTitle={labels.KnowledgeItemCreate}
        entity={this.state.knowledgeItem}
        show={this.props.show}
        handleClose={this.props.handleClose}
        disabled={this.state.disabled}
        onChange={this.handleDialogChange}
        onSave={this.onCreateData}
        maxLength={maxLength}
        messageHandler={this.messageHandler}
        handleEntered={() => this.handleEntered()}
        additionalInputFields={this.inputFields()}
        validationRules={validationRules.knowledgeItemBasic}
        documentRef={this.documentRef}
        setStartUploading={this.setStartUploading}
        childrenFirst
      >
        {this.state.searchSimilar ? (
          <SearchSimilar
            isInDialog
            messageHandler={this.props.messageHandler}
            entity={this.state.entitySimilarSearch}
          />
        ) : null}
      </BasicDialog>
    );
  }
}

export default withKeycloak(withRouter(KnowledgeItemBasicContainer));
